import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatBytes } from '@/lib/format';
import AttachmentUpload from '@/pages/apps/components/attachment-upload';
import RemoveAttachment from '@/pages/apps/components/remove-attachment';
import type { Attachment, ListField } from '@/types/platform';
import { FileText } from 'lucide-react';

interface AttachmentsFieldProps {
  appId: string;
  // False until the app's setup has finished. Nothing can be added before then.
  provisioned: boolean;
  recordId: string;
  field: ListField;
  attachments: Attachment[];
}

// One attachments field on a saved record: what is there, and a way to add more.
const AttachmentsField = ({
  appId,
  provisioned,
  recordId,
  field,
  attachments,
}: AttachmentsFieldProps) => {
  const images = attachments.filter((attachment) => attachment.image);
  const files = attachments.filter((attachment) => !attachment.image);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{field.label}</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {attachments.length === 0 ? (
          <p className="text-muted-foreground text-sm">Nothing added yet.</p>
        ) : null}

        {images.length > 0 ? (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {images.map((attachment) => (
              <li key={attachment.id} className="min-w-0 space-y-1">
                <a href={attachment.url} target="_blank" rel="noreferrer" className="block">
                  <img
                    src={attachment.url}
                    alt={attachment.filename}
                    loading="lazy"
                    className="bg-muted aspect-square w-full rounded-md border object-cover"
                  />
                </a>
                <div className="flex items-center justify-between gap-1">
                  <span className="text-muted-foreground truncate text-xs">
                    {attachment.filename}
                  </span>
                  <RemoveAttachment appId={appId} attachment={attachment} />
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {files.length > 0 ? (
          <ul className="divide-y rounded-md border">
            {files.map((attachment) => (
              <li key={attachment.id} className="flex items-center gap-3 px-3 py-2">
                <FileText className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 flex-1 truncate text-sm font-medium underline"
                >
                  {attachment.filename}
                </a>
                <span className="text-muted-foreground shrink-0 text-xs">
                  {formatBytes(attachment.byteSize)}
                </span>
                <RemoveAttachment appId={appId} attachment={attachment} />
              </li>
            ))}
          </ul>
        ) : null}

        <AttachmentUpload
          appId={appId}
          provisioned={provisioned}
          recordId={recordId}
          fieldKey={field.key}
          inputId={`attachment-${field.key}`}
          label={`Add to ${field.label.toLowerCase()}`}
        />
      </CardContent>
    </Card>
  );
};

export default AttachmentsField;
